import type { GanttIndicadores } from "@/shared/components/gantt-carga/gantt-carga.constants";
import {
  formatarIdentificacaoObra,
  identificacaoDaObra,
  type IdentificacaoObra,
} from "@/shared/utils/formatar-identificacao-obra";
import {
  assignLanes,
  formatDayMonth,
  isPast,
  monthEnd,
  monthStart,
  positionInterval,
} from "@/modules/carga-trabalho";
import type {
  CargaTrabalhoIntervalo,
  CargaTrabalhoPeriodo,
  IsoDate,
  Timeline,
} from "@/modules/carga-trabalho";
import {
  conflitosAbertos,
  conflitosDoLocal,
  conflitosPorAlocacaoDeLocal,
  describeConflito,
  formatDuracao,
  OBRA_SITUACAO_CANCELADA,
} from "@/modules/carga-de-trabalho";
import type {
  AlocacaoDeObra,
  AlocacoesPorLocal,
  AlocacoesPorObra,
  ConflitoLocal,
  ObraResumo,
  StatusAlocacao,
} from "@/modules/carga-de-trabalho";
import { pilulaDaObra, rangeLine } from "./gantt-rows";
import type { GanttItem } from "./gantt-rows";
import type { ObraLine, ObraPill } from "./gantt-obras-rows";

export const SEM_LOCAL_LABEL = "Sem Local";

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Pílula de uma obra sem Local (não tem período, então não ocupa posição no eixo). */
export interface LocalChip {
  key: string;
  /** `ID - sigla`: o `title` da pílula. */
  label: string;
  /** Nome e sigla da obra: o texto visível, escolhido pela largura (ADR 0018). */
  rotulo?: IdentificacaoObra;
  cancelada: boolean;
}

/** Linha do card de conflito: "start" (inicia depois, vermelha) ou "end" (termina, azul). */
export interface ConflictLine {
  label: string;
  event: string;
  tone: "start" | "end";
}

/** Um grupo por conflito, com as duas obras envolvidas. */
export interface ConflictGroup {
  lines: ConflictLine[];
}

/** Faixa laranja (conflito) ou verde (tratado) do trecho; carrega o conflito, para o card. */
export interface LocalBand {
  key: string;
  tone: "conflito" | "tratado";
  left: number;
  width: number;
  group: ConflictGroup;
}

export interface LocalRowModel {
  key: string;
  kind: "local" | "sem-local";
  title: string;
  cidade?: string;
  uf?: string;
  meta: string | null;
  /** Conflitos em aberto (os tratados não contam no selo nem permitem resolução). */
  conflicts: number;
  /** Todos os conflitos do Local, abertos e tratados (alimentam o modal de resolução). */
  conflitos: ConflitoLocal[];
  items: GanttItem[];
  bands: LocalBand[];
  chips: LocalChip[];
  lanes: number;
}

/** Uma linha da visão por Obra: uma alocação (ou a obra sem Local). */
export interface LocalObraRowModel {
  key: string;
  obraKey: string;
  /** Primeira linha da obra: a única que mostra a identificação dela. */
  first: boolean;
  obraCodigo: string;
  obraNome: string;
  /** `ID - nome da embarcação` e `ID - sigla` (ADR 0018). */
  obraRotulo: IdentificacaoObra;
  localNome: string;
  /** `null` na linha "Sem Local". */
  status: StatusAlocacao | null;
  hasAlocacao: boolean;
  conflicts: number;
  bands: LocalBand[];
  line: ObraLine | null;
  pill: ObraPill | null;
}

export function isCancelada(obra: Pick<ObraResumo, "situacao">): boolean {
  return obra.situacao === OBRA_SITUACAO_CANCELADA;
}

/** O indicador "Cancelada" controla as canceladas; "Obra", as demais. */
function isVisible(obra: Pick<ObraResumo, "situacao">, indicadores: GanttIndicadores): boolean {
  return isCancelada(obra) ? indicadores.cancelada : indicadores.obra;
}

/** Estende o período (em meses inteiros) até cobrir todas as alocações; nunca o encolhe. */
export function extendPeriodoLocal(
  periodo: CargaTrabalhoPeriodo,
  data: AlocacoesPorLocal | AlocacoesPorObra | null,
): CargaTrabalhoPeriodo {
  if (!data) return periodo;

  const dates: IsoDate[] = [];
  if ("locais" in data) {
    for (const local of data.locais) {
      for (const alocacao of local.alocacoes) dates.push(alocacao.inicio, alocacao.fim);
    }
  } else {
    for (const obra of data.obras) {
      for (const alocacao of obra.alocacoes) dates.push(alocacao.inicio, alocacao.fim);
    }
  }

  const valid = dates.filter((date) => ISO_DATE.test(date)).sort();
  const first = valid[0];
  const last = valid[valid.length - 1];
  if (!first || !last) return periodo;

  return {
    inicio: first < periodo.inicio ? monthStart(first.slice(0, 7)) : periodo.inicio,
    fim: last > periodo.fim ? monthEnd(last.slice(0, 7)) : periodo.fim,
  };
}

/**
 * Faixas (sombreado) dos trechos em conflito. Cada faixa carrega o grupo do conflito — as
 * duas obras e as datas — que o card exibe ao passar o mouse na área laranja dentro do Gantt.
 */
function buildConflictBands(
  conflitos: ConflitoLocal[],
  timeline: Timeline,
  prefix: string,
): LocalBand[] {
  const bands: LocalBand[] = [];

  conflitos.forEach((conflito, index) => {
    const position = positionInterval(conflito, timeline);
    if (!position) return;

    const linhas = describeConflito(conflito);
    bands.push({
      key: `${prefix}-${index}`,
      tone: conflito.tratado ? "tratado" : "conflito",
      left: position.left,
      width: position.width,
      group: {
        lines: linhas.map((linha, order) => ({
          label: linha.obra,
          event: linha.evento,
          tone: order === 0 ? "start" : "end",
        })),
      },
    });
  });

  return bands;
}

function alocacaoTooltip(
  obra: ObraResumo,
  intervalo: CargaTrabalhoIntervalo,
  local?: string,
): string {
  return [
    `Obra ${formatarIdentificacaoObra(obra)}`,
    ...(local ? [`Local: ${local}`] : []),
    ...(isCancelada(obra) ? ["Situação: Cancelada"] : []),
    rangeLine("Alocação", intervalo),
  ].join("\n");
}

/** Barra de um período só (sem etapas), igual à da Profissionais. */
function buildBar(
  alocacao: AlocacaoDeObra,
  timeline: Timeline,
  today: IsoDate,
): Omit<GanttItem, "lane"> | null {
  const position = positionInterval(alocacao, timeline);
  if (!position) return null;
  const cancelada = isCancelada(alocacao.obra);

  return {
    key: alocacao.id,
    kind: "bar",
    left: position.left,
    width: position.width,
    ...pilulaDaObra(alocacao.obra),
    past: !cancelada && isPast(alocacao, today),
    cancelada,
    segments: [{ kind: cancelada ? "cancelada" : "obra", left: 0, width: 100 }],
    tooltip: alocacaoTooltip(alocacao.obra, alocacao),
  };
}

/** Perspectiva Local agrupada por Local: uma linha por Local e, ao fim, "Sem Local". */
export function buildLocalRows(
  data: AlocacoesPorLocal,
  timeline: Timeline,
  indicadores: GanttIndicadores,
  today: IsoDate,
): LocalRowModel[] {
  const rows: LocalRowModel[] = data.locais.map((local) => {
    const conflitos = conflitosDoLocal(local.alocacoes, data.conflitosIgnorados);
    const entries = local.alocacoes
      .filter((alocacao) => isVisible(alocacao.obra, indicadores))
      .flatMap((alocacao) => {
        const item = buildBar(alocacao, timeline, today);
        return item ? [{ item, intervalo: alocacao }] : [];
      });

    const lanes = assignLanes(entries.map((entry) => entry.intervalo));
    const bands = buildConflictBands(conflitos, timeline, local.id);
    // O contador segue os indicadores, não a janela do timeline: alocação fora dela continua contando.
    const total = local.alocacoes.filter((alocacao) =>
      isVisible(alocacao.obra, indicadores),
    ).length;

    return {
      key: local.id,
      kind: "local",
      title: local.nome,
      cidade: local.cidade,
      uf: local.uf,
      meta: `${total} ${total === 1 ? "Alocação" : "Alocações"}`,
      conflicts: conflitosAbertos(conflitos).length,
      conflitos,
      items: entries.map((entry, index) => ({ ...entry.item, lane: lanes[index] ?? 0 })),
      bands,
      chips: [],
      lanes: Math.max(1, ...lanes.map((lane) => lane + 1)),
    };
  });

  if (data.semLocal.length > 0) {
    rows.push({
      key: "sem-local",
      kind: "sem-local",
      title: SEM_LOCAL_LABEL,
      meta: null,
      conflicts: 0,
      conflitos: [],
      items: [],
      bands: [],
      chips: data.semLocal
        .filter((obra) => isVisible(obra, indicadores))
        .map((obra) => ({
          key: obra.id,
          ...pilulaDaObra(obra),
          cancelada: isCancelada(obra),
        })),
      lanes: 1,
    });
  }

  return rows;
}

/** Perspectiva Local agrupada por Obra: uma linha por alocação (ou "Sem Local"). */
export function buildLocalObraRows(
  data: AlocacoesPorObra,
  timeline: Timeline,
  indicadores: GanttIndicadores,
  today: IsoDate,
): LocalObraRowModel[] {
  const conflitosByAlocacao = conflitosPorAlocacaoDeLocal(data.obras);

  return data.obras
    .filter((obra) => isVisible(obra, indicadores))
    .flatMap((obra) => {
      const cancelada = isCancelada(obra);
      const alocacoes = obra.alocacoes.length > 0 ? obra.alocacoes : [null];

      return alocacoes.map((alocacao, index): LocalObraRowModel => {
        const base = {
          obraKey: obra.id,
          first: index === 0,
          obraCodigo: obra.codigo,
          obraNome: obra.nome,
          obraRotulo: identificacaoDaObra(obra),
        };

        if (!alocacao) {
          return {
            ...base,
            key: `${obra.id}-sem-local`,
            localNome: SEM_LOCAL_LABEL,
            status: null,
            hasAlocacao: false,
            conflicts: 0,
            bands: [],
            line: null,
            pill: null,
          };
        }

        const conflitos = conflitosByAlocacao.get(alocacao.id) ?? [];
        const bands = buildConflictBands(conflitos, timeline, alocacao.id);
        const position = positionInterval(alocacao, timeline);
        const past = isPast(alocacao, today);
        const detail = alocacaoTooltip(obra, alocacao, alocacao.local.nome);

        return {
          ...base,
          key: alocacao.id,
          localNome: alocacao.local.nome,
          status: alocacao.status,
          hasAlocacao: true,
          conflicts: conflitos.length,
          bands,
          // A linha com as datas só existe para obra ativa; a cancelada mostra só a pílula.
          line:
            position && !cancelada
              ? {
                  left: position.left,
                  width: position.width,
                  start: formatDayMonth(alocacao.inicio),
                  end: formatDayMonth(alocacao.fim),
                  tooltip: detail,
                  past,
                }
              : null,
          pill: position
            ? {
                left: position.left,
                width: position.width,
                segments: [{ kind: cancelada ? "cancelada" : "obra", left: 0, width: 100 }],
                label: formatDuracao(alocacao.inicio, alocacao.fim),
                tooltip: detail,
                past: !cancelada && past,
                cancelada,
              }
            : null,
        };
      });
    });
}
